import React from 'react';
import { useTranslation } from 'next-i18next';
import { ArrowRight, Circle } from 'lucide-react';
import { Button, Card, Skeleton } from '@gu-corp/gu-ui';
import Link from 'next/link';
import { AppRoutesEnum } from '~/enums/route.enum';
import { LendingData } from '~/hooks/with-lending-data';
import { renderBalance } from '~/utils/render.util';

interface Props {
  title: string;
  durationTitle: string;
  link: AppRoutesEnum;
  data: LendingData | null;
  error?: boolean;
  descriptions: string[];
  'data-testid'?: string;
}

const LendingPlanItem = (props: Props) => {
  const { title, durationTitle, data, error, descriptions, link } = props;
  const { t } = useTranslation('common');

  const isDisable =
    error ||
    (data?.totalDepositedTokens || BigInt(0)) >= (data?.maximumDepositTokens || BigInt(0)) ||
    (data?.minimumLoanAmount || BigInt(100)) > (data?.remainingDepositTokens || BigInt(0));

  return (
    <Card className="p-6 flex flex-col h-full" data-testid={props['data-testid']}>
      <div className="mb-4">
        <span className="text-sm font-medium text-primary" data-testid="lending-plan-type">
          {title}
        </span>
        <h3
          className="text-xl font-semibold text-foreground mt-1"
          data-testid="lending-plan-duration"
        >
          {durationTitle}
        </h3>
      </div>

      <div className="mb-4">
        {error ? (
          <div className="flex items-baseline gap-2">
            <span
              className="text-2xl font-bold text-muted-foreground"
              data-testid="lending-plan-amount-left"
            >
              - / -
            </span>
            <span className="text-sm text-muted-foreground" data-testid="lending-plan-currency">
              JOC
            </span>
          </div>
        ) : data ? (
          <div className="flex items-baseline gap-2">
            <span
              className="text-2xl font-bold text-foreground"
              data-testid="lending-plan-amount-left"
            >
              {`${renderBalance(data.totalDepositedTokens || BigInt(0))} / ${renderBalance(
                data.maximumDepositTokens || BigInt(0),
              )}`}
            </span>
            <span className="text-sm text-muted-foreground" data-testid="lending-plan-currency">
              JOC
            </span>
          </div>
        ) : (
          <Skeleton className="h-8 w-48" />
        )}
      </div>

      <div className="space-y-2 flex-1">
        {descriptions.map((description, index) => (
          <div key={index} className="flex items-start gap-2">
            <Circle className="w-2 h-2 mt-2 fill-current text-muted-foreground flex-shrink-0" />
            <span
              className="text-sm text-muted-foreground"
              data-testid={`lending-plan-description-${index + 1}`}
            >
              {description}
            </span>
          </div>
        ))}
      </div>

      <div className="mt-6">
        <Link href={link}>
          <Button disabled={isDisable} className="w-full" data-testid="lending-plan-select-button">
            {t('select')}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      </div>
    </Card>
  );
};

export default LendingPlanItem;
